<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Table from 'dashboard/components/table/Table.vue';
import ReportsFiltersDateRange from './Filters/DateRange.vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_OPTIONS } from '../constants';

const store = useStore();
const { t } = useI18n();

const from = ref(0);
const to = ref(0);

const teams = useMapGetter(['teams/getTeams']);
const ticketReports = useMapGetter(['summaryReports/getTeamTicketsSummaryReports']);

const getMetrics = id =>
  ticketReports.value.find(r => r.id === Number(id)) || {};

const columnHelper = createColumnHelper();

const renderCount = value => (value != null ? value.toLocaleString() : '--');

const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t('SUMMARY_REPORTS.TEAM'),
    width: 300,
  }),
  columnHelper.accessor('ticketsCount', {
    header: t('TEAM_REPORTS.TICKETS_TABLE.TICKETS_COUNT'),
    width: 200,
  }),
  columnHelper.accessor('resolvedTicketsCount', {
    header: t('TEAM_REPORTS.TICKETS_TABLE.RESOLVED_TICKETS'),
    width: 200,
  }),
]);

const tableData = computed(() =>
  teams.value.map(team => {
    const metrics = getMetrics(team.id);
    return {
      id: team.id,
      name: team.name,
      ticketsCount: renderCount(metrics.ticketsCount),
      resolvedTicketsCount: renderCount(metrics.resolvedTicketsCount),
    };
  })
);

const fetchData = () => {
  store.dispatch('teams/get');
  store.dispatch('summaryReports/fetchTeamTicketsSummaryReports', {
    since: from.value,
    until: to.value,
  });
};

onMounted(() => {
  const fromDate = subDays(new Date(), DATE_RANGE_OPTIONS.LAST_7_DAYS.offset);
  from.value = getUnixStartOfDay(fromDate);
  to.value = getUnixEndOfDay(new Date());
  fetchData();
});

const onDateRangeChange = selectedRange => {
  const fromDate = subDays(new Date(), selectedRange.offset);
  from.value = getUnixStartOfDay(fromDate);
  to.value = getUnixEndOfDay(new Date());
  fetchData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});
</script>

<template>
  <ReportsFiltersDateRange @on-range-change="onDateRangeChange" />
  <div
    class="flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <Table :table="table" />
  </div>
</template>
